import Image from 'next/image';
import { Dispatch, SetStateAction } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { Button } from '../../../ui';

export const EBITDA = ({
  capitalRequestInput,
  handleChange,
  setCurrentStep,
  isLoading,
}: {
  capitalRequestInput: CapitalRequestInput;
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  handleChange: (
    key: string,
    value:
      | boolean
      | {
          min: number;
          max: number;
          unknown: boolean;
          unprovided: boolean;
        }
  ) => void;
  isLoading: boolean;
}) => {
  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold">EBITDA</span>
          <span className="text-base font-normal">
            Does your organization have positive EBITDA (Earnings Before
            Interest Taxes Depreciation and Amortization)?
          </span>
        </div>
        <div className="flex gap-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="ebitda-positive"
              value="positive"
              className="text-blue-600 form-radio"
              checked={capitalRequestInput.positiveEBITDA}
              onChange={(e) =>
                e.target.checked && handleChange('positiveEBITDA', true)
              }
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              name="ebitda-positive"
              value="negative"
              className="text-blue-600 form-radio"
              checked={!capitalRequestInput.positiveEBITDA}
              onChange={(e) =>
                e.target.checked && handleChange('positiveEBITDA', false)
              }
            />
            <span className="ml-2">No</span>
          </label>
        </div>
        {capitalRequestInput.positiveEBITDA && (
          <div className="flex flex-col gap-2">
            <span className="text-base font-normal">
              What is your current EBITDA?
            </span>
            <div className="flex flex-col gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="positive"
                  className="text-blue-600 form-radio"
                  defaultChecked={
                    capitalRequestInput.EBITDARange.min === 0 &&
                    capitalRequestInput.EBITDARange.max === 1
                  }
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 0,
                      max: 1,
                      unknown: false,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">Less than $1 million</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={
                    capitalRequestInput.EBITDARange.min === 1 &&
                    capitalRequestInput.EBITDARange.max === 2.9
                  }
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 1,
                      max: 2.9,
                      unknown: false,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">$1 million - $2.9 million</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={
                    capitalRequestInput.EBITDARange.min === 3 &&
                    capitalRequestInput.EBITDARange.max === 4.9
                  }
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 3,
                      max: 4.9,
                      unknown: false,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">$3 million - $4.9 million</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={
                    capitalRequestInput.EBITDARange.min === 5 &&
                    capitalRequestInput.EBITDARange.max === 9.9
                  }
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 5,
                      max: 9.9,
                      unknown: false,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">$5 million - $9.9 million</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={
                    capitalRequestInput.EBITDARange.min === 10 &&
                    capitalRequestInput.EBITDARange.max === 0
                  }
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 10,
                      max: 0,
                      unknown: false,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">$10 million or more</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={capitalRequestInput.EBITDARange.unprovided}
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 0,
                      max: 0,
                      unknown: false,
                      unprovided: true,
                    })
                  }
                />
                <span className="ml-2">Decline to provide</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="ebitda"
                  value="negative"
                  className="text-blue-600 form-radio"
                  defaultChecked={capitalRequestInput.EBITDARange.unknown}
                  onChange={(e) =>
                    e.target.checked &&
                    handleChange('EBITDARange', {
                      min: 0,
                      max: 0,
                      unknown: true,
                      unprovided: false,
                    })
                  }
                />
                <span className="ml-2">Do not know</span>
              </label>
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('OWNERSHIP')}
            />
          </div>
          <div>
            <Button
              text="Continue"
              variant={isLoading ? 'disabled' : 'primary'}
              suffixIcon="/icons/arrow_right.svg"
              onClick={() => setCurrentStep('AMOUNT NEEDED')}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src="/create_cr.png"
          alt="Supplier Request Image"
          priority
          width={512}
          height={584}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
